import { supabase } from '../lib/supabase';
import puterAI from '../lib/puter';
import { conversationAnalyzerService } from './practiceService';
import { getCoachingPersonality } from './lumoPersonalityService';
import gamificationService from './gamificationService';
import lumoUpsellService from './lumoUpsellService';

/**
 * Lumo Conversation Service
 * Handles intelligent conversational interactions with Lumo AI mentor
 * Provides context-aware coaching, feature triggers, and natural language understanding
 */

export const lumoConversationService = {
  /**
   * Send a message to Lumo and get intelligent response
   * @param {string} message - User's message
   * @param {Object} context - Current context (page, user state, etc.)
   * @returns {Promise<Object>} Lumo's response with actions
   */
  async sendMessage(message, context = {}) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user profile for personality
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      // Get conversation history
      const conversationHistory = await this.getConversationHistory(user.id);

      // Classify intent
      const intent = await this.classifyIntent(message, context);

      // Generate response based on intent
      const response = await this.generateResponse(message, intent, context, profile, conversationHistory);

      // Save conversation
      await this.saveMessage(user.id, message, response, context);

      return response;
    } catch (error) {
      console.error('Error in Lumo conversation:', error);
      return {
        type: 'error',
        message: "Sorry, I'm having trouble right now. Can you try asking that again?",
        actions: []
      };
    }
  },

  /**
   * Classify user intent from message
   * @param {string} message - User's message
   * @param {Object} context - Current context
   * @returns {Promise<Object>} Intent classification
   */
  async classifyIntent(message, context) {
    const lowerMessage = message?.toLowerCase()?.trim();

    // Greeting patterns - Lumo should respond warmly
    if (lowerMessage.match(/^(hi|hey|hello|sup|yo|good morning|good afternoon|good evening)/i)) {
      return { type: 'greeting', action: 'greet', confidence: 1.0 };
    }

    // Emotional check-ins - User sharing how they feel
    if (lowerMessage.match(/(how are you|how's it going|what's up)/i)) {
      return { type: 'greeting', action: 'reciprocal_greeting', confidence: 1.0 };
    }

    // User responding to Lumo's check-in
    if (lowerMessage.match(/^(good|great|bad|terrible|ok|fine|not great|struggling|tired|pumped|excited|nervous|stressed)/i)) {
      return { type: 'emotional_response', action: 'acknowledge_emotion', confidence: 0.9 };
    }

    // Motivation requests
    if (lowerMessage.match(/(motivate me|need motivation|pump me up|give me a pep talk|feeling down|discouraged)/i)) {
      return { type: 'motivation', action: 'motivate', confidence: 1.0 };
    }

    // Subscription/upgrade questions - route to upsell
    if (lowerMessage.match(/(upgrade|subscription|pricing|plans|features|paid|premium|pro|elite|unlock|price|cost|worth it)/i)) {
      return { type: 'upsell', action: 'explore_upgrade', confidence: 0.95 };
    }

    // Command patterns
    if (lowerMessage.match(/^(let's|start|begin) (roleplay|practice|role play)/i)) {
      return { type: 'command', action: 'roleplay', confidence: 0.9 };
    }
    
    // Script generation requests (more flexible matching)
    if (lowerMessage.match(/(write|generate|create|draft|make|need|help.*(making|writing|creating)).*(email|script|sms|text message|message|voicemail|pitch)/i)) {
      return { type: 'command', action: 'script', confidence: 0.9 };
    }
    
    if (lowerMessage.match(/^(give me|show me|practice|do|try) (a |an )?(drill|objection|challenge)/i)) {
      return { type: 'command', action: 'drill', confidence: 0.9 };
    }
    
    if (lowerMessage.match(/(how am i doing|my progress|my performance|my stats|how's my)/i)) {
      return { type: 'feedback', action: 'performance', confidence: 0.9 };
    }

    // Question patterns
    if (lowerMessage.match(/^(what|how|why|when|where|who|explain|tell me about)/i) ||
        lowerMessage.includes('?')) {
      return { type: 'question', action: 'explain', confidence: 0.8 };
    }

    // Drill help patterns
    if (lowerMessage.match(/(hint|help|stuck|don't know|not sure|struggling)/i)) {
      if (context?.page === '/daily-drill' || context?.page === '/objection-dojo') {
        return { type: 'help', action: 'hint', confidence: 0.9 };
      }
      return { type: 'help', action: 'general', confidence: 0.7 };
    }

    // Default to general conversation
    return { type: 'conversation', action: 'chat', confidence: 0.5 };
  },

  /**
   * Generate response based on intent
   * @param {string} message - User's message
   * @param {Object} intent - Classified intent
   * @param {Object} context - Current context
   * @param {Object} profile - User profile
   * @param {Array} history - Conversation history
   * @returns {Promise<Object>} Response object
   */
  async generateResponse(message, intent, context, profile, history) {
    const personality = getCoachingPersonality(profile?.skill_level || 'beginner');

    switch (intent.type) {
      case 'greeting':
        return await this.handleGreeting(intent.action, profile, personality, history);
      
      case 'emotional_response':
        return await this.handleEmotionalResponse(message, profile, personality);
      
      case 'motivation':
        return await this.handleMotivation(profile, personality);
      
      case 'upsell':
        return await this.handleUpgradeQuestion(profile, personality);
      
      case 'command':
        return await this.handleCommand(intent.action, message, context, profile, personality);
      
      case 'question':
        return await this.handleQuestion(message, context, profile, personality);
      
      case 'feedback':
        return await this.handleFeedbackRequest(intent.action, profile, personality);
      
      case 'help':
        return await this.handleHelpRequest(intent.action, context, profile, personality);
      
      case 'conversation':
      default:
        return await this.handleConversation(message, context, profile, personality, history);
    }
  },

  /**
   * Handle greetings - Lumo checks in like a mentor
   */
  async handleGreeting(action, profile, personality, history) {
    const firstName = profile?.full_name?.split(' ')[0] || 'there';
    const level = profile?.level || 1;
    const streak = await gamificationService.getStreak(profile?.id);
    const currentStreak = streak?.currentStreak || 0;
    
    const timeOfDay = new Date().getHours();
    const greeting = timeOfDay < 12 ? 'Good morning' : timeOfDay < 18 ? 'Good afternoon' : 'Good evening';
    
    // Check if first conversation today
    const lastMessage = history?.[0];
    const isFirstToday = !lastMessage || new Date(lastMessage.created_at).toDateString() !== new Date().toDateString();

    if (action === 'greet') {
      let message = '';
      
      if (personality.name === 'Mentor Mode') {
        message = `${greeting}, ${firstName}! 🌟\n\n`;
        
        if (isFirstToday) {
          message += `How are you feeling today? Ready to work on your skills?\n\n`;
          
          if (currentStreak > 0) {
            message += `You're on a ${currentStreak}-day streak—that's awesome! Let's keep it going. 💪\n\n`;
          }
          
          message += `What would you like to focus on today?`;
        } else {
          message += `Good to see you back! What can I help you with?`;
        }
        
        return {
          type: 'greeting',
          message,
          actions: [
            { label: '💪 Practice Drills', type: 'navigate', path: '/app/daily-drill' },
            { label: '🎭 Roleplay', type: 'navigate', path: '/app/ai-roleplay' },
            { label: '📊 Check My Progress', type: 'navigate', path: '/app/gamification' }
          ]
        };
      } else if (personality.name === 'Challenger Mode') {
        message = `${greeting}, ${firstName}. 🎯\n\n`;
        
        if (isFirstToday) {
          message += `How's your headspace today? Let's make this session count.\n\n`;
          message += `What are we working on?`;
        } else {
          message += `Back for more? Good. What's next?`;
        }
        
        return {
          type: 'greeting',
          message,
          actions: [
            { label: 'Start Practice', type: 'navigate', path: '/app/daily-drill' },
            { label: 'Compete with Peers', type: 'navigate', path: '/app/gamification?tab=challenges' }
          ]
        };
      } else {
        message = `${firstName}. Let's go. 💥\n\n`;
        message += `What are we working on today?`;
        
        return {
          type: 'greeting',
          message,
          actions: [
            { label: "Let's Practice", type: 'navigate', path: '/app/daily-drill' },
            { label: 'Show Stats', type: 'chat', message: 'How am I doing?' }
          ]
        };
      }
    } else if (action === 'reciprocal_greeting') {
      // User asked "how are you?"
      let message = '';
      
      if (personality.name === 'Mentor Mode') {
        message = `I'm doing great, thanks for asking! 😊\n\nMore importantly—how are YOU doing today? Feeling ready to practice, or is there something on your mind?`;
      } else if (personality.name === 'Challenger Mode') {
        message = `I'm ready to push you harder. The real question is—are you ready to show up?\n\nHow are you feeling about your game today?`;
      } else {
        message = `I'm here to work. Now, how are YOU showing up today?\n\nReady to level up or what?`;
      }
      
      return {
        type: 'greeting',
        message,
        actions: []
      };
    }
  },

  /**
   * Handle emotional responses - Lumo responds empathetically
   */
  async handleEmotionalResponse(message, profile, personality) {
    const lowerMessage = message.toLowerCase();
    const firstName = profile?.full_name?.split(' ')[0] || 'champ';
    
    // Positive emotions
    if (lowerMessage.match(/^(good|great|awesome|fantastic|pumped|excited|ready|energized)/i)) {
      let response = '';
      
      if (personality.name === 'Mentor Mode') {
        response = `That's what I love to hear! 🔥 Your energy is everything in sales.\n\nLet's channel that into some focused practice. When you're feeling this good, that's when real breakthroughs happen!\n\nWhat do you want to tackle first?`;
      } else if (personality.name === 'Challenger Mode') {
        response = `Good. Use that energy. High energy = high performance.\n\nLet's not waste it. What's the goal for this session?`;
      } else {
        response = `Perfect. That's the mindset. Now let's put it to work.\n\nWhat are we crushing today?`;
      }
      
      return {
        type: 'emotional_ack',
        message: response,
        actions: [
          { label: 'Start Drill', type: 'navigate', path: '/app/daily-drill' },
          { label: 'Roleplay Session', type: 'navigate', path: '/app/ai-roleplay' }
        ]
      };
    }
    
    // Negative emotions
    if (lowerMessage.match(/^(bad|terrible|stressed|tired|struggling|nervous|anxious|not great|rough|tough)/i)) {
      let response = '';
      
      if (personality.name === 'Mentor Mode') {
        response = `I hear you, ${firstName}. Some days are harder than others, and that's completely normal. 💙\n\nHere's the truth: showing up when it's hard? That's when you build real mental toughness.\n\nYou don't need to be perfect today—just present. Even 10 minutes of focused practice counts.\n\nWant to start with something light, or talk through what's on your mind?`;
      } else if (personality.name === 'Challenger Mode') {
        response = `Rough day, huh? I get it. But here's the thing—pros practice when they don't feel like it.\n\nYou can either let this feeling win, or you can show up anyway and prove to yourself you're stronger than the mood.\n\nSmall session. Low pressure. Let's just get 1 rep in. Deal?`;
      } else {
        response = `Feeling off? Join the club. Every elite performer has bad days.\n\nDifference is: they show up anyway. Not asking for your A-game today—just asking you to show up.\n\nOne drill. That's it. Can you do that?`;
      }
      
      return {
        type: 'emotional_support',
        message: response,
        actions: [
          { label: 'Do One Quick Drill', type: 'navigate', path: '/app/daily-drill' },
          { label: 'Maybe Later', type: 'chat', message: 'I need a break' }
        ]
      };
    }
    
    // Neutral/OK responses
    let response = '';
    
    if (personality.name === 'Mentor Mode') {
      response = `Fair enough! Some days we're not firing on all cylinders, and that's okay.\n\nConsistent, steady practice beats waiting for the "perfect" day. Let's make today count anyway.\n\nWhat sounds doable right now?`;
    } else {
      response = `Alright. Let's make the most of it. You don't need to feel amazing to get work done.\n\nWhat are we doing?`;
    }
    
    return {
      type: 'neutral_ack',
      message: response,
      actions: [
        { label: 'Practice', type: 'navigate', path: '/app/daily-drill' }
      ]
    };
  },

  /**
   * Handle motivation requests - Lumo provides personalized motivation
   */
  async handleMotivation(profile, personality) {
    const firstName = profile?.full_name?.split(' ')[0] || 'champ';
    const level = profile?.level || 1;
    const lifetimeXp = profile?.lifetime_xp || 0;
    
    let message = '';
    
    if (personality.name === 'Mentor Mode') {
      message = `Listen, ${firstName}—I've watched you put in the work. Level ${level} doesn't happen by accident. 💪\n\n`;
      message += `Every drill you complete, every objection you practice, every time you show up when you don't feel like it—that's building a version of you that WILL succeed.\n\n`;
      message += `Sales is a skill. Skills are built through repetition. You're doing it right.\n\n`;
      message += `The prospects out there don't know what's coming. You're getting sharper every single day. Keep going. 🚀`;
    } else if (personality.name === 'Challenger Mode') {
      message = `You want motivation? Here it is:\n\n`;
      message += `You're at level ${level}. That puts you ahead of 80% of people who started and quit.\n\n`;
      message += `But "ahead" doesn't mean "done." The reps who win aren't the most talented—they're the most consistent.\n\n`;
      message += `You've got ${lifetimeXp.toLocaleString()} lifetime XP. That's proof you can do hard things.\n\n`;
      message += `Now stop reading this and go practice. Motivation fades. Discipline wins. Let's go. 💥`;
    } else {
      message = `Real talk, ${firstName}:\n\n`;
      message += `Nobody's coming to save you. Your quota? Your responsibility. Your skills? Your responsibility.\n\n`;
      message += `But here's the good news: you've already proven you can level up. Level ${level} says you're built different.\n\n`;
      message += `Now the question is—are you going to keep that same energy or coast?\n\n`;
      message += `Champions practice when they don't feel like it. Are you a champion or not? 🔥`;
    }
    
    return {
      type: 'motivation',
      message,
      actions: [
        { label: "Let's Practice Now", type: 'navigate', path: '/app/daily-drill' },
        { label: 'Show Me My Progress', type: 'navigate', path: '/app/gamification' }
      ]
    };
  },

  /**
   * Handle upgrade/subscription questions
   */
  async handleUpgradeQuestion(profile, personality) {
    const currentPlan = profile?.subscription_plan || 'free';
    const level = profile?.level || 1;
    const firstName = profile?.full_name?.split(' ')[0] || 'there';

    // Get contextual upsell based on current plan
    let upsellContext;
    if (currentPlan === 'free') {
      upsellContext = level >= 10 ? 'level_milestone' : 'exploring_premium';
    } else if (currentPlan === 'pro') {
      upsellContext = 'drill_mastery';
    } else {
      // Already Elite - no upsell needed
      return {
        type: 'info',
        message: `${firstName}, you're already on our **Elite plan**—you have access to everything! 🎉\n\nYou've got:\n✅ Unlimited drills\n✅ AI Roleplay\n✅ Script Lab\n✅ Objection Dojo\n✅ Advanced analytics\n\nIs there a specific feature you want to explore?`,
        actions: [
          { label: 'Try AI Roleplay', type: 'navigate', path: '/app/ai-roleplay' },
          { label: 'Objection Dojo', type: 'navigate', path: '/app/objection-dojo' },
          { label: 'Generate Script', type: 'chat', message: 'Write me an email script' }
        ]
      };
    }

    // Get strategic upsell
    const streak = await gamificationService.getStreak();
    const stats = {
      streak: streak?.currentStreak || 0,
      level: level
    };

    const upsell = lumoUpsellService.getStrategicUpsell(profile, upsellContext, stats);
    
    if (!upsell) {
      return {
        type: 'info',
        message: `Good question! Let me show you what's available with our premium plans.`,
        actions: [
          { label: 'View Plans', type: 'navigate', path: '/app/subscription' }
        ]
      };
    }

    // Format the upsell message
    const message = `${upsell.hook}\n\n${upsell.pain}\n\n**Here's what you get:**\n${upsell.value}\n\n*${upsell.social_proof}*${upsell.urgency ? `\n\n⚡ ${upsell.urgency}` : ''}`;

    // Mark upsell shown
    lumoUpsellService.markUpsellShown(upsellContext);

    return {
      type: 'upsell',
      message,
      actions: [
        { label: upsell.cta, type: 'navigate', path: '/app/subscription' },
        { label: 'What Can I Do Now?', type: 'chat', message: 'What features do I have access to?' }
      ]
    };
  },

  /**
   * Handle command intents (analyze, roleplay, script, drill)
   */
  async handleCommand(action, message, context, profile, personality) {
    // Check if feature requires premium access and show strategic upsell
    const featureAccess = this.checkFeatureAccess(action, profile);
    
    if (!featureAccess.hasAccess) {
      return await this.handleUpsell(featureAccess.upsellContext, profile, personality);
    }

    switch (action) {
      case 'roleplay': {
        // Parse persona type from message
        const personas = ['skeptical', 'rushed', 'friendly', 'hostile', 'analytical', 'indecisive'];
        const foundPersona = personas.find(p => message.toLowerCase().includes(p));
        const persona = foundPersona || 'skeptical';

        return {
          type: 'roleplay_start',
          message: `${personality.greeting} Perfect! I'll play a **${persona}** buyer. You're selling ${profile?.industry || 'your product'}.\n\nI'll throw realistic objections your way. Your goal: build rapport, handle objections, and move toward a close.\n\nReady to start?`,
          actions: [
            { label: "Let's Go!", type: 'navigate', path: `/app/ai-roleplay?persona=${persona}` },
            { label: 'Choose Different Persona', type: 'chat', message: 'Show me all personas' }
          ]
        };
      }

      case 'script': {
        // Parse script type
        const scriptTypes = {
          'email': 'email',
          'sms': 'sms',
          'text': 'sms',
          'voicemail': 'voicemail',
          'pitch': 'pitch',
          'follow': 'follow_up'
        };
        
        const scriptType = Object.keys(scriptTypes).find(type => 
          message.toLowerCase().includes(type)
        );

        if (scriptType) {
          return {
            type: 'script_generate',
            message: `Got it! I'll help you write a ${scriptTypes[scriptType]} script.\n\nTell me:\n1️⃣ Who's the prospect?\n2️⃣ What's your goal?\n3️⃣ Any specific details?`,
            actions: [
              { label: 'Go to Script Lab', type: 'navigate', path: '/app/script-lab' }
            ],
            awaitingDetails: true,
            scriptType: scriptTypes[scriptType]
          };
        } else {
          return {
            type: 'prompt',
            message: "What type of script do you need?\n\n📧 Email\n📱 SMS\n📞 Voicemail\n🎯 Pitch\n✉️ Follow-up",
            actions: []
          };
        }
      }

      case 'drill': {
        // Check for specific objection type
        const objectionTypes = ['price', 'timing', 'interest', 'authority', 'budget', 'competition'];
        const requestedType = objectionTypes.find(type => message.toLowerCase().includes(type));
        
        // Check if user has access to objection dojo (PRO tier)
        const currentPlan = profile?.subscription_plan || 'free';
        const hasPro = currentPlan.toLowerCase() !== 'free';
        
        // Route based on access level
        const canAccessObjectionDojo = hasPro && requestedType;

        return {
          type: 'drill_start',
          message: requestedType 
            ? hasPro 
              ? `Let's practice **${requestedType}** objections! I've got some tough ones for you. 💪`
              : `${requestedType.charAt(0).toUpperCase() + requestedType.slice(1)} objections? Great! The Objection Dojo is a Pro feature, but I can still give you practice drills. 💪`
            : `Time to practice! I'll give you a real-world scenario and you craft your best response.\n\nReady?`,
          actions: [
            { 
              label: canAccessObjectionDojo ? 'Go to Objection Dojo' : 'Start Drill', 
              type: 'navigate', 
              path: canAccessObjectionDojo ? `/objection-dojo?type=${requestedType}` : '/daily-drill' 
            },
            !hasPro && requestedType ? 
              { label: 'Unlock Objection Dojo', type: 'navigate', path: '/app/subscription' } : null
          ].filter(Boolean)
        };
      }

      default:
        return await this.handleConversation(message, context, profile, personality, []);
    }
  },

  /**
   * Handle questions (coaching concepts, frameworks, techniques)
   */
  async handleQuestion(message, context, profile, personality) {
    // Check cache first
    const cachedAnswer = await this.getCachedAnswer(message);
    if (cachedAnswer) {
      return {
        type: 'answer',
        message: cachedAnswer.answer_text,
        actions: cachedAnswer.actions || []
      };
    }

    // Generate AI answer
    try {
      const systemPrompt = `You are Lumo, an AI sales coach. ${personality.systemPrompt}

The user asked: "${message}"

Provide a helpful, concise answer (2-3 paragraphs max). If it's about a sales framework or technique, explain it clearly with an example.

Format your response in markdown. Be encouraging and actionable.`;

      const response = await puterAI.generateContent(systemPrompt, {
        model: 'claude-sonnet-4-5',
        temperature: 0.7
      });

      const answerText = response?.message?.content?.[0]?.text || response?.text || "I'm not sure about that. Can you rephrase?";

      // Cache the answer
      await this.cacheAnswer(message, answerText);

      return {
        type: 'answer',
        message: answerText,
        actions: [
          { label: 'Practice This', type: 'navigate', path: '/daily-drill' },
          { label: 'Learn More Concepts', type: 'navigate', path: '/coaching-concepts' }
        ]
      };
    } catch (error) {
      console.error('Error generating answer:', error);
      return {
        type: 'error',
        message: "I'm having trouble answering that right now. Try asking in a different way?",
        actions: []
      };
    }
  },

  /**
   * Handle performance/feedback requests
   */
  async handleFeedbackRequest(action, profile, personality) {
    try {
      // Get user stats
      const streak = await gamificationService.getStreak();
      const missions = await gamificationService.getTodaysMissions();
      const completedMissions = missions?.filter(m => m.isCompleted)?.length || 0;

      // Get this week's performance
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - 7);

      const { data: recentDrills } = await supabase
        .from('user_drills')
        .select('lumo_overall_score, created_at')
        .gte('created_at', weekStart.toISOString())
        .order('created_at', { ascending: false })
        .limit(20);

      const avgScore = recentDrills?.length
        ? Math.round(recentDrills.reduce((sum, d) => sum + (d.lumo_overall_score || 0), 0) / recentDrills.length)
        : 0;

      const drillCount = recentDrills?.length || 0;

      // Format response based on personality
      let message = '';
      
      if (personality.name === 'Mentor Mode') {
        message = `Great to see you checking in! 🌟\n\n**This Week:**\n📊 ${drillCount} drills completed\n⭐ ${avgScore}% average score\n🔥 ${streak?.currentStreak || 0}-day streak\n✅ ${completedMissions}/3 missions done\n\n`;
        if (avgScore >= 80) {
          message += "You're doing awesome! Keep this momentum going! 💪";
        } else {
          message += "You're making progress! Remember, consistency beats perfection. Keep showing up! 🎯";
        }
      } else if (personality.name === 'Challenger Mode') {
        message = `Let's see what you've been up to... 🎯\n\n**This Week:**\n📊 ${drillCount} drills\n⭐ ${avgScore}% avg\n🔥 ${streak?.currentStreak || 0}-day streak\n✅ ${completedMissions}/3 missions\n\n`;
        if (avgScore >= 85) {
          message += "Solid work. Now let's push for 90%+ consistency. You're capable of more.";
        } else {
          message += "You're showing up, but your scores need work. Focus on quality over quantity.";
        }
      } else {
        message = `Here's the real talk. 💥\n\n**This Week:**\n📊 ${drillCount} drills\n⭐ ${avgScore}% avg\n🔥 ${streak?.currentStreak || 0}-day streak\n\n`;
        if (avgScore >= 90) {
          message += "You're operating at elite level. Keep that standard.";
        } else {
          message += "You're better than these scores show. Stop coasting. Push harder.";
        }
      }

      const currentPlan = profile?.subscription_plan || 'free';
      const isElite = currentPlan.toLowerCase() === 'elite' || currentPlan.toLowerCase() === 'team';
      
      return {
        type: 'performance',
        message,
        actions: [
          isElite ? 
            { label: 'View Full Stats', type: 'navigate', path: '/gamification' } :
            { label: 'Upgrade for Stats', type: 'navigate', path: '/app/subscription' },
          { label: 'Practice Now', type: 'navigate', path: '/daily-drill' }
        ],
        data: { avgScore, drillCount, streak: streak?.currentStreak || 0, missions: completedMissions }
      };
    } catch (error) {
      console.error('Error fetching performance:', error);
      return {
        type: 'error',
        message: "I couldn't load your stats right now. Try again in a moment?",
        actions: []
      };
    }
  },

  /**
   * Handle help requests (hints, guidance)
   */
  async handleHelpRequest(action, context, profile, personality) {
    if (action === 'hint' && context?.drillId) {
      // Get drill details and provide hint
      return {
        type: 'hint',
        message: `${personality.feedbackTemplates.opening}\n\n💡 **Hint:** Focus on the 4 C's:\n\n✅ **Clarity** - Be specific and easy to follow\n✅ **Confidence** - Sound like you believe it\n✅ **Control** - Ask questions, don't just pitch\n✅ **Close** - Always include a next step\n\nTry incorporating these into your response!`,
        actions: []
      };
    }

    // General help
    const currentPlan = profile?.subscription_plan || 'free';
    const hasStarter = currentPlan.toLowerCase() !== 'free';
    
    return {
      type: 'help',
      message: `I'm here to help! Here's what I can do:\n\n💪 **Practice** drills and objections\n📈 **Track** your progress\n💡 **Explain** frameworks and techniques\n\n**Premium Features:**\n🎭 **Roleplay** with AI personas (Elite)\n✍️ **Generate** scripts and templates (Pro)\n\nWhat do you need help with?`,
      actions: [
        { label: 'Start a Drill', type: 'navigate', path: '/daily-drill' },
        hasStarter ? 
          { label: 'Learn a Framework', type: 'navigate', path: '/coaching-concepts' } :
          { label: 'View Premium', type: 'navigate', path: '/app/subscription' },
        { label: 'Ask Me Anything', type: 'focus_input' }
      ]
    };
  },

  /**
   * Handle general conversation
   */
  async handleConversation(message, context, profile, personality, history) {
    try {
      const conversationContext = history?.slice(-5)?.map(h => 
        `User: ${h.user_message}\nLumo: ${h.lumo_response}`
      )?.join('\n\n');

      const systemPrompt = `You are Lumo, an AI sales coach in Earn My Edge. ${personality.systemPrompt}

Current context:
- User is on: ${context?.page || 'unknown page'}
- User level: ${profile?.level || 1}
- User industry: ${profile?.industry || 'General'}

Recent conversation:
${conversationContext || 'This is the start of the conversation.'}

User just said: "${message}"

Respond naturally and helpfully. Keep it conversational (2-3 sentences). If appropriate, suggest an action they can take.`;

      const response = await puterAI.generateContent(systemPrompt, {
        model: 'claude-sonnet-4-5',
        temperature: 0.8
      });

      const responseText = response?.message?.content?.[0]?.text || response?.text || "I'm here to help! What can I do for you?";

      return {
        type: 'conversation',
        message: responseText,
        actions: this.suggestActionsForContext(context, profile)
      };
    } catch (error) {
      console.error('Error in conversation:', error);
      return {
        type: 'conversation',
        message: personality.greeting + " How can I help you level up today?",
        actions: this.suggestActionsForContext(context, profile)
      };
    }
  },

  /**
   * Suggest contextual actions based on current page
   */
  suggestActionsForContext(context, profile = {}) {
    const currentPlan = profile?.subscription_plan || 'free';
    const isElite = currentPlan.toLowerCase() === 'elite' || currentPlan.toLowerCase() === 'team';
    
    const actions = {
      '/daily-drill': [
        { label: 'Get Hint', type: 'chat', message: 'Give me a hint' },
        { label: 'Explain Framework', type: 'chat', message: 'What frameworks should I use?' }
      ],
      '/objection-dojo': [
        { label: 'Practice Price Objection', type: 'navigate', path: '/objection-dojo?type=price' },
        { label: 'Get Tips', type: 'chat', message: 'How do I handle objections?' }
      ],
      default: [
        { label: 'Start Practice', type: 'navigate', path: '/daily-drill' },
        isElite ? 
          { label: 'View Progress', type: 'navigate', path: '/gamification' } :
          { label: 'View Premium', type: 'navigate', path: '/app/subscription' }
      ]
    };

    return actions[context?.page] || actions.default;
  },

  /**
   * Format analysis response
   */
  formatAnalysisResponse(analysis, personality) {
    const score = analysis?.overall_score || 0;
    let opener = '';

    if (personality.name === 'Mentor Mode') {
      opener = score >= 80 ? "Nice work! 🌟" : "Good effort! Let's make it better. 💪";
    } else if (personality.name === 'Challenger Mode') {
      opener = score >= 85 ? "Solid." : "There's room for improvement here.";
    } else {
      opener = score >= 90 ? "Strong." : "You can do better than this.";
    }

    return `${opener}\n\n**Overall Score:** ${score}/100\n\n**Top Strength:** ${analysis?.strengths?.[0] || 'N/A'}\n**Top Gap:** ${analysis?.improvements?.[0] || 'N/A'}\n\nClick "View Full Analysis" for the complete breakdown!`;
  },

  /**
   * Get conversation history for user
   */
  async getConversationHistory(userId) {
    try {
      const { data } = await supabase
        .from('lumo_conversations')
        .select('messages')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      return data?.messages || [];
    } catch (error) {
      return [];
    }
  },

  /**
   * Save message to conversation history
   */
  async saveMessage(userId, userMessage, lumoResponse, context) {
    try {
      // Get existing conversation or create new
      const { data: existing } = await supabase
        .from('lumo_conversations')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      const newMessage = {
        timestamp: new Date().toISOString(),
        user_message: userMessage,
        lumo_response: lumoResponse.message,
        actions: lumoResponse.actions || [],
        intent: lumoResponse.type,
        context
      };

      if (existing) {
        // Append to existing conversation (keep last 50 messages for performance)
        const updatedMessages = [...(existing.messages || []), newMessage].slice(-50);
        await supabase
          .from('lumo_conversations')
          .update({ 
            messages: updatedMessages,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);
      } else {
        // Create new conversation
        await supabase
          .from('lumo_conversations')
          .insert({
            user_id: userId,
            messages: [newMessage],
            context
          });
      }
    } catch (error) {
      console.error('Error saving conversation:', error);
    }
  },

  /**
   * Get cached answer for common questions
   */
  async getCachedAnswer(question) {
    try {
      // Simple hash of question (normalize for caching)
      const normalizedQuestion = question.toLowerCase().trim().replace(/[^a-z0-9\s]/g, '');
      const questionHash = btoa(normalizedQuestion).substring(0, 50);

      const { data } = await supabase
        .from('lumo_qa_cache')
        .select('*')
        .eq('question_hash', questionHash)
        .single();

      if (data) {
        // Increment hit count
        await supabase
          .from('lumo_qa_cache')
          .update({ hit_count: (data.hit_count || 0) + 1 })
          .eq('id', data.id);
        
        return data;
      }

      return null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Cache answer for future use
   */
  async cacheAnswer(question, answer, category = 'general') {
    try {
      const normalizedQuestion = question.toLowerCase().trim().replace(/[^a-z0-9\s]/g, '');
      const questionHash = btoa(normalizedQuestion).substring(0, 50);

      await supabase
        .from('lumo_qa_cache')
        .insert({
          question_hash: questionHash,
          question_text: question,
          answer_text: answer,
          category,
          hit_count: 1
        });
    } catch (error) {
      console.error('Error caching answer:', error);
    }
  },

  /**
   * Check if user has access to feature and determine upsell context
   * @param {string} action - Command action
   * @param {Object} profile - User profile
   * @returns {Object} Access status and upsell context
   */
  checkFeatureAccess(action, profile) {
    const currentPlan = profile?.subscription_plan || 'free';
    
    // Map plan names to normalized values
    const normalizedPlan = currentPlan.toLowerCase();
    const isPro = normalizedPlan === 'pro' || normalizedPlan === 'elite' || normalizedPlan === 'team';
    const isElite = normalizedPlan === 'elite' || normalizedPlan === 'team';
    
    const featureGates = {
      roleplay: { 
        requiredPlan: 'elite',
        upsellContext: 'roleplay_locked',
        hasAccess: isElite
      },
      script: {
        requiredPlan: 'pro',
        upsellContext: 'script_locked',
        hasAccess: isPro
      },
      drill: {
        requiredPlan: 'free',
        upsellContext: null,
        hasAccess: true // Drills available to all (daily-drill is free)
      }
    };

    const feature = featureGates[action] || { hasAccess: true, upsellContext: null };
    return feature;
  },

  /**
   * Handle strategic upsell based on context
   * @param {string} context - Upsell trigger context
   * @param {Object} profile - User profile
   * @param {Object} personality - Coaching personality
   * @returns {Promise<Object>} Upsell message
   */
  async handleUpsell(context, profile, personality) {
    // Check if appropriate to show upsell
    if (!lumoUpsellService.canShowUpsell(context)) {
      // Too soon for another upsell - provide gentle alternative
      return {
        type: 'feature_locked',
        message: `That feature requires an upgrade. Want to see what else you can do right now instead?`,
        actions: [
          { label: 'See Upgrade Options', type: 'navigate', path: '/app/subscription' },
          { label: 'Show Free Features', type: 'chat', message: 'What can I do for free?' }
        ]
      };
    }

    // Get streak and stats for contextual upsell
    const streak = await gamificationService.getStreak();
    const stats = {
      streak: streak?.currentStreak || 0,
      level: profile?.level || 1
    };

    // Get strategic upsell
    const upsell = lumoUpsellService.getStrategicUpsell(profile, context, stats);
    
    if (!upsell) {
      return {
        type: 'feature_locked',
        message: `This feature requires a premium plan. Check out our upgrade options!`,
        actions: [
          { label: 'View Plans', type: 'navigate', path: '/app/subscription' }
        ]
      };
    }

    // Format upsell message
    const upsellMessage = lumoUpsellService.formatUpsellMessage(upsell);
    
    // Mark upsell shown
    lumoUpsellService.markUpsellShown(context);
    
    // Track upsell event
    await lumoUpsellService.trackUpsellEvent('upsell_shown', {
      context,
      tier: upsell.tier,
      currentPlan: profile?.subscription_plan || 'free',
      userLevel: profile?.level || 1
    });

    return upsellMessage;
  }
};

export default lumoConversationService;
